import { Flex, Skeleton } from 'antd';
import { useOneManufacturer } from 'api/hooks/manufacturers/useOneManufacturer';
import { Button, Card, Descriptions, Form, PageHeader } from 'components';
import { useHistory, useParams } from 'react-router-dom';
import { type CreateManufacturerDto, RoleName, type UpdateManufacturerDto } from '@enmon/commons/lib/tools';
import { useUpdateManufacturer } from 'api/hooks/manufacturers/useUpdateManufacturer';
import { useUserInfo } from '@/hooks/useUser';
import { useCreateManufacturer } from 'api/hooks/manufacturers/useCreateManufacturer';
import { getManufacturerColumns } from './utils/getColumns';
import { MANUFACTURER_FORM_ID } from './constants';
import { MeterModelsTable } from './components/Table';
import { MeterModelsFilter } from './components/Filter';
import { ModelLibraryTab, modelLibraryBreadcrumbs } from '@/routes/reseller-data/routes/model-library/const';

type ManufacturerFormValues = CreateManufacturerDto | UpdateManufacturerDto;

export const ManufacturerDetailPage = () => {
  const { id: manufacturerId } = useParams<{ id: string }>();
  const history = useHistory();
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  // Data
  const { data: manufacturerData, isFetching: isLoadingManufacturer } = useOneManufacturer(Number(manufacturerId));
  const { mutate: updateManufacturer, isLoading: isUpdatingManufacturer } = useUpdateManufacturer();
  const { mutate: createManufacturer, isLoading: isCreatingManufacturer } = useCreateManufacturer();

  // Handlers
  const handleCreateManufacturer = (values: CreateManufacturerDto) => {
    createManufacturer(values, {
      onSuccess: responseData => {
        history.replace(`/reseller-data/model-library/manufacturer/${responseData.id}`);
      },
    });
  };

  const handleUpdateManufacturer = (values: UpdateManufacturerDto) => {
    if (!manufacturerId) throw new Error('Manufacturer ID is not defined in URL param');

    updateManufacturer({
      id: Number(manufacturerId),
      values,
    });
  };

  // Form
  const [form] = Form.useForm<ManufacturerFormValues>();

  if (isLoadingManufacturer || isUpdatingManufacturer || isCreatingManufacturer) {
    return <Skeleton active />;
  }

  return (
    <Flex vertical gap={8}>
      <PageHeader
        breadcrumbs={modelLibraryBreadcrumbs(
          ModelLibraryTab.Manufacturer,
          manufacturerId ? manufacturerData?.name : 'Create new manufacturer',
        )}
        extra={
          isMagicRole && (
            <Button form={MANUFACTURER_FORM_ID} htmlType="submit" loading={isUpdatingManufacturer} type="primary">
              Save
            </Button>
          )
        }
        ghost={false}
        title={manufacturerId ? manufacturerData?.name : 'Create new manufacturer'}
        onBack={() => history.goBack()}
      />

      <Card>
        <Form<ManufacturerFormValues>
          form={form}
          id={MANUFACTURER_FORM_ID}
          onFinish={values =>
            manufacturerId
              ? handleUpdateManufacturer(values)
              : handleCreateManufacturer(values as CreateManufacturerDto)
          }
        >
          <Descriptions
            column={1}
            columns={getManufacturerColumns()}
            data={manufacturerData ?? {}}
            isEditing={isMagicRole}
            labelWidth={150}
            layout="vertical"
            size="small"
          />
        </Form>
      </Card>
      {manufacturerId && (
        <>
          <Card title="Meter models">
            <MeterModelsFilter />
          </Card>
          <Card>
            <MeterModelsTable />
          </Card>
        </>
      )}
    </Flex>
  );
};
